import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./AddProduct.css";
import { useTitle } from "../../hooks/useTitle";
import {
  tablesDB, storage, DB_ID, PRODUCTS_TABLE, BUCKET_ID,
} from "../../appwrite";
import { ID } from "appwrite";
import { ImagePlus, X, Star } from "lucide-react";

const generateSlug = (name) =>
  name.toString().toLowerCase().replace(/[^\w\s]/g, "").replace(/\s+/g, "-").replace(/-+$/g, "");

export default function EditProduct() {
  useTitle("Edit Product - TrendBay Seller");
  const navigate = useNavigate();
  const { id } = useParams();

  const [product, setProduct] = useState({
    productName: "", productDescription: "", productCondition: "",
    price: "", quantity: "", sku: "", category: "", available: true, featured: false,
  });
  // Existing images from DB: [{ preview: url, file: null }]
  // New uploaded images: [{ preview: dataUrl, file: File }]
  const [images, setImages] = useState([]);
  const [saving, setSaving] = useState(false);
  const imageInputRef = useRef();

  useEffect(() => {
    const meta = document.querySelector("meta[name='theme-color']");
    const orig = meta?.getAttribute("content");
    if (meta) meta.setAttribute("content", "#080F0D");
    return () => { if (meta) meta.setAttribute("content", orig); };
  }, []);

  useEffect(() => {
    tablesDB.getRow({ databaseId: DB_ID, tableId: PRODUCTS_TABLE, rowId: id })
      .then((row) => {
        setProduct(row);
        // Pre-populate image grid from existing URLs
        const existing = [];
        if (row.productImage) existing.push({ preview: row.productImage, file: null, url: row.productImage });
        (row.moreImages || []).forEach((url) => existing.push({ preview: url, file: null, url }));
        setImages(existing);
      })
      .catch(() => toast.error("Failed to load product. Please go back and try again."));
  }, [id]);

  const formatPrice = (val) => {
    const n = parseFloat((val || "").toString().replace(/,/g, ""));
    return isNaN(n) ? "" : n.toLocaleString("en-NG", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  const handleField = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "price") {
      setProduct((p) => ({ ...p, price: formatPrice(value) }));
    } else if (type === "checkbox") {
      setProduct((p) => ({ ...p, [name]: checked }));
    } else if (name === "productName") {
      setProduct((p) => ({ ...p, productName: value, slug: generateSlug(value) }));
    } else {
      setProduct((p) => ({ ...p, [name]: value }));
    }
  };

  const readFile = (file) =>
    new Promise((resolve) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result);
      r.readAsDataURL(file);
    });

  const addImages = async (files) => {
    const entries = await Promise.all(
      Array.from(files).map(async (file) => ({ file, preview: await readFile(file), url: null }))
    );
    setImages((prev) => [...prev, ...entries]);
  };

  const removeImage = (idx) => setImages((prev) => prev.filter((_, i) => i !== idx));

  const makeCover = (idx) => {
    setImages((prev) => {
      const next = [...prev];
      const [picked] = next.splice(idx, 1);
      return [picked, ...next];
    });
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    if (e.dataTransfer.files.length) await addImages(e.dataTransfer.files);
  };

  const handleSave = async () => {
    if (!product.productName || !product.productDescription || !product.price || !product.sku || !product.category || images.length === 0) {
      toast.error("Fill in all required fields and keep at least one image.");
      return;
    }
    setSaving(true);
    try {
      // Upload only new images; keep existing URLs as-is
      const urls = await Promise.all(
        images.map(async (img) => {
          if (img.file) {
            const fileId = ID.unique();
            await storage.createFile(BUCKET_ID, fileId, img.file);
            return `https://storage.trendbayds.com/f/${fileId}`;
          }
          return img.url;
        })
      );

      const [productImage, ...moreImages] = urls;

      const { $id, $createdAt, $updatedAt, $permissions, $databaseId, $collectionId, ...dataToUpdate } = {
        ...product,
        price: parseFloat(product.price.toString().replace(/,/g, "")),
        quantity: parseInt(product.quantity || "0", 10),
        productImage,
        moreImages,
      };

      await tablesDB.updateRow({
        databaseId: DB_ID,
        tableId: PRODUCTS_TABLE,
        rowId: id,
        data: dataToUpdate,
      });

      toast.success("Product updated!");
      navigate("/s/my-vault");
    } catch (e) {
      console.error(e);
      toast.error("Error updating product. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ap-page">

      {/* Header */}
      <div className="ap-header">
        <div>
          <h2 className="ap-title">Edit Product</h2>
          <p className="ap-subtitle">Update your listing details</p>
        </div>
        <div className="ap-header-actions">
          <button className="ap-btn-ghost" onClick={() => navigate("/s/my-vault")}>Discard</button>
          <button className="ap-btn-green" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>

      <div className="ap-body">

        {/* LEFT */}
        <div className="ap-main">

          {/* Images */}
          <div className="ap-card">
            <h3 className="ap-card-title">Product Images</h3>
            <p className="ap-card-hint">First image is the cover. Click ★ to make any image the cover.</p>
            <div
              className="ap-image-drop"
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => imageInputRef.current.click()}
            >
              <ImagePlus size={28} color="#00A855" />
              <p>Drop images here or <span>click to browse</span></p>
              <p className="ap-drop-hint">Add new images or keep existing ones</p>
            </div>
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: "none" }}
              onChange={(e) => addImages(e.target.files)}
            />
            {images.length > 0 && (
              <div className="ap-image-grid">
                {images.map((img, idx) => (
                  <div key={idx} className={`ap-thumb ${idx === 0 ? "ap-thumb--cover" : ""}`}>
                    <img src={img.preview} alt="" />
                    {idx === 0 && <span className="ap-thumb-badge">Cover</span>}
                    <div className="ap-thumb-actions">
                      {idx !== 0 && (
                        <button className="ap-thumb-btn ap-thumb-star" onClick={() => makeCover(idx)} title="Make cover">
                          <Star size={13} />
                        </button>
                      )}
                      <button className="ap-thumb-btn ap-thumb-remove" onClick={() => removeImage(idx)} title="Remove">
                        <X size={13} />
                      </button>
                    </div>
                  </div>
                ))}
                <div className="ap-thumb ap-thumb--add" onClick={() => imageInputRef.current.click()}>
                  <ImagePlus size={22} color="#aaa" />
                  <span>Add more</span>
                </div>
              </div>
            )}
          </div>

          {/* Product info */}
          <div className="ap-card">
            <h3 className="ap-card-title">Product Information</h3>
            <div className="ap-field">
              <label className="ap-label">Title <span className="ap-req">*</span></label>
              <input className="ap-input" type="text" name="productName" value={product.productName} onChange={handleField} placeholder="e.g. Nike Air Max 90" />
            </div>
            <div className="ap-field">
              <label className="ap-label" style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Description <span className="ap-req">*</span></span>
                <span style={{ fontWeight: 400, color: (product.productDescription || "").length > 950 ? "#e74c3c" : "#bbb" }}>
                  {(product.productDescription || "").length}/1000
                </span>
              </label>
              <textarea
                className="ap-input ap-textarea"
                name="productDescription"
                value={product.productDescription || ""}
                onChange={handleField}
                rows={6}
                maxLength={1000}
                placeholder="Describe your item…"
              />
            </div>
          </div>

        </div>

        {/* RIGHT */}
        <div className="ap-side">

          {/* Pricing */}
          <div className="ap-card">
            <h3 className="ap-card-title">Pricing & Details</h3>

            <div className="ap-price-qty-row">
              <div className="ap-field ap-price-col">
                <label className="ap-label">Price (NGN) <span className="ap-req">*</span></label>
                <div className="ap-price-wrap">
                  <span className="ap-currency">₦</span>
                  <input className="ap-input ap-input--price" type="text" name="price" value={product.price} onChange={handleField} placeholder="0" />
                </div>
              </div>
              <div className="ap-field ap-qty-col">
                <label className="ap-label">Qty</label>
                <input className="ap-input" type="number" name="quantity" value={product.quantity || ""} onChange={handleField} placeholder="0" min="0" />
              </div>
            </div>

            <div className="ap-field">
              <label className="ap-label">Category <span className="ap-req">*</span></label>
              <select className="ap-input" name="category" value={product.category || ""} onChange={handleField}>
                <option value="">Select Category</option>
                <option value="reup-vault">ReUp Vault</option>
              </select>
            </div>

            <div className="ap-field">
              <label className="ap-label">SKU</label>
              <input className="ap-input ap-input--muted" type="text" name="sku" value={product.sku || ""} readOnly />
            </div>

            <div className="ap-field">
              <label className="ap-label">Condition</label>
              <select className="ap-input" name="productCondition" value={product.productCondition || ""} onChange={handleField}>
                <option value="">Select Condition</option>
                <option value="new">New</option>
                <option value="like-new">Like New / No Box</option>
                <option value="used">Used</option>
                <option value="for-parts">For Parts</option>
              </select>
            </div>
          </div>

          {/* Visibility */}
          <div className="ap-card">
            <h3 className="ap-card-title">Visibility</h3>
            <div className="ap-toggle-row">
              <div>
                <p className="ap-toggle-label">Available for sale</p>
                <p className="ap-toggle-hint">Visible to buyers</p>
              </div>
              <label className="ap-switch">
                <input type="checkbox" name="available" checked={!!product.available} onChange={handleField} />
                <span className="ap-switch-track" />
              </label>
            </div>
          </div>

          <div className="ap-side-actions">
            <button className="ap-btn-green ap-btn-full" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}
